import { EditFinancial, RequestFinancial } from '@insurance/api/requests'
import {
  deviceSession,
  extractSeriesAndNumber,
  formatDateToISOFormat
} from '@shared/utils'
import { NamePath } from 'antd/es/form/interface'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

import { FinancialFormValues, FinancialPersonFormValue } from './type'
import { getContractorFields, getCustomerFields } from '../PersonsFields'

export const INSURANCE_FORM_DATA_KEY = '__financial_insurance_form_data__'
export const MIN_AGE = 18

export const formProps = (tFunction?: TFunction) => {
  const t = tFunction ?? (() => '')

  return {
    stepKey: { name: 'step' },
    priceKey: { name: 'price' },
    orderKey: { name: 'order_id' },
    validFromProps: { name: 'valid_from', label: t('valid_from') },
    validToProps: { name: 'valid_to', label: t('valid_to') },
    territoryProps: { name: 'territory', label: t('insurance_territory') },
    personsCountProps: { name: 'persons_count', label: t('persons_count') },
    coefficientProps: {
      name: 'correction_coefficient',
      label: t('correction_coefficient')
    },
    coefficientMinKey: { name: 'coefficient_min' },
    coefficientMaxKey: { name: 'coefficient_max' },
    contractorProps: { name: 'contractor', label: t('contractor') },
    customerProps: { name: 'customer', label: t('customer') },
    personsProps: { name: 'persons' },
    priceDataKey: { name: 'data' }
  } satisfies Record<string, { name: NamePath; label?: React.ReactNode }>
}

/** Everything the premium is built from — no contractor, no persons. */
const normalizePricingData = (values: FinancialFormValues) => ({
  valid_from: formatDateToISOFormat(values.valid_from)!,
  valid_to: formatDateToISOFormat(values.valid_to)!,
  persons_count: values.persons_count,
  ...(values.territory && { territory: values.territory }),
  ...(values.correction_coefficient && {
    correction_coefficient: String(values.correction_coefficient)
  })
})

export const normalizeCommonData = (
  values: FinancialFormValues
): RequestFinancial => {
  const session = deviceSession()

  return {
    ...normalizePricingData(values),
    session: {
      device_id: session.deviceId,
      session_id: session.sessionId
    }
  } as RequestFinancial
}

/**
 * Payload for a recalculation triggered from within the form. `persons` is left
 * out on purpose — it is replace-all, so sending the half-filled list would wipe
 * what is already stored; the stored one stays and only the pricing inputs move.
 */
export const normalizeRecalculateData = (
  values: FinancialFormValues
): EditFinancial => normalizePricingData(values) as unknown as EditFinancial

export const normalizeEditOrderData = (
  values: FinancialFormValues,
  isInitPayment = true
): EditFinancial => {
  return {
    valid_from: formatDateToISOFormat(values.valid_from)!,
    valid_to: formatDateToISOFormat(values.valid_to)!,
    ...(values.territory && { territory: values.territory }),
    contractor: getContractorFields({
      contractor: values.contractor,
      same_contractor_as_first_person: values.same_contractor_as_first_person,
      persons: values.persons
    } as unknown as Parameters<typeof getContractorFields>[0]),
    ...(isInitPayment && { customer: getCustomerFields(values.customer) }),
    persons: values.persons?.map((person) => ({
      identification_number: person?.identification_number,
      first_name: person?.first_name,
      last_name: person?.last_name,
      date_of_birth: formatDateToISOFormat(person?.date_of_birth)!,

      ...(person?.document && {
        document: extractSeriesAndNumber(person?.document)!
      })
    })),
    ...(values.correction_coefficient && {
      correction_coefficient: String(values.correction_coefficient)
    })
  } as EditFinancial
}

export const saveFinancialValuesToStorage = (
  values: FinancialFormValues,
  id: string | number = ''
) => {
  sessionStorage.setItem(id + INSURANCE_FORM_DATA_KEY, JSON.stringify(values))
}

export const getFinancialValuesFromStorage = (id: string | number = '') => {
  const values = sessionStorage.getItem(id + INSURANCE_FORM_DATA_KEY)

  if (!values) return

  const parsedValues = JSON.parse(values) as FinancialFormValues

  return {
    ...parsedValues,
    ...(parsedValues.valid_from && {
      valid_from: dayjs(parsedValues.valid_from)
    }),
    ...(parsedValues.valid_to && { valid_to: dayjs(parsedValues.valid_to) }),
    persons: parsedValues.persons?.map((person) => ({
      ...person,
      ...(person?.date_of_birth && {
        date_of_birth: dayjs(person.date_of_birth)
      })
    }))
  }
}

export const removeFinancialValuesFromStorage = (id: string | number = '') =>
  sessionStorage.removeItem(id + INSURANCE_FORM_DATA_KEY)

export const resizePersons = (
  persons: FinancialPersonFormValue[] = [],
  count = 1
): FinancialPersonFormValue[] => {
  if (persons.length === count) return persons
  if (persons.length > count) return persons.slice(0, count)

  return [
    ...persons,
    ...Array.from(
      { length: count - persons.length },
      () => ({} as FinancialPersonFormValue)
    )
  ]
}
